// SceneEditor.jsx
import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";

/**
 * SceneEditor (Material-UI v4)
 * - value: scene object { arSceneDescription, active, arNodes: [] }
 * - onChange: (nextScene) => void
 *
 * NodeTypes: video | button
 * nodeAnimations: ONLY for button
 
 Prinzip:Alles klar — dann machen wir jetzt den sauberen “Roundtrip”:
markerAR = JSON-String (wird gespeichert / ans Backend geschickt)
markerARtemp = Objekt (damit der SceneEditor komfortabel editieren kann)
Beim Öffnen (Edit) → markerAR parsen → markerARtemp
Beim Ändern im Editor → markerARtemp updaten und gleichzeitig markerAR = JSON.stringify(...)
 
 
 */

const NODE_TYPES = ["video", "button"];
const ANIMATION_TYPES = ["transition", "rotation", "opacity"];



const emptyPosition = () => ({ x: 0, y: 0, z: 0 });
const emptyDimension = () => ({ width: 40, height: 40 });
const emptyAngle = () => ({ x: 0, y: 0, z: 0 });

function makeDefaultNode(type = "button") {
  const base = {
    nodeName: "",
    nodeId: Date.now().toString(),
    nodeType: type,
    nodeActive: true,
  };

  if (type === "video") {
    return {
      ...base,
      nodeAttributes: {
        src: "",
        audiosrc: "",
        audio: false,
        maximize: true,
        externalLink: "",
        position: emptyPosition(),
      },
    };
  }

  return {
    ...base,
    nodeAttributes: {
      src: "",
      opacity: 1,
      bgrcolor: "#000000",
      maximize: true,
      externalLink: "",
      position: emptyPosition(),
      dimension: emptyDimension(),
    },
    nodeAnimations: [],
  };
}

function makeDefaultAnimation(type = "transition") {
  if (type === "transition") {
    return {
      animationType: "transition",
      duration: 4.0,
      fromPosition: { x: 0, y: 0, z: 0 },
      toPosition: { x: 0, y: 0, z: 0 },
      autoreverses: false,
      repeatCount: 1,
      isRemovedOnCompletion: false,
    };
  }
  if (type === "rotation") {
    return {
      animationType: "rotation",
      duration: 12.0,
      fromAngle: emptyAngle(),
      toAngle: { x: 0, y: 0, z: 90 },
      repeatCount: 1,
    };
  }
  return {
    animationType: "opacity",
    duration: 5.0,
    fromOpacity: 0.0,
    toOpacity: 1.0,
    repeatCount: 1,
  };
}

function num(v, fallback = 0) {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function FieldText({ label, value, onChange, placeholder }) {
  return (
    <TextField
      fullWidth
      variant="standard"
      color="primary"
      margin="dense"
      label={label}
      value={value ?? ""}
      placeholder={placeholder ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function FieldNumber({ label, value, onChange, step = 1 }) {
  return (
    <TextField
      fullWidth
      variant="standard"
      color="primary"
      margin="dense"
      label={label}
      type="number"
      inputProps={{ step }}
      value={num(value, 0)}
      onChange={(e) => onChange(Number(e.target.value))}
    />
  );
}

function FieldSelect({ label, value, options, onChange }) {
  return (
    <Box mt={1}>
      <Typography variant="caption" style={{ display: "block", opacity: 0.8 }}>
        {label}
      </Typography>
      <Select
        fullWidth
        variant="standard"
          
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <MenuItem key={o} value={o}>
            {o}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

function PositionEditor({ label, pos, onChange }) {
  const p = pos ?? emptyPosition();
  return (
    <Box mt={1}>
      <Typography variant="subtitle2">{label}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <FieldNumber label="x" value={p.x} onChange={(v) => onChange({ ...p, x: v })} />
        </Grid>
        <Grid item xs={4}>
          <FieldNumber label="y" value={p.y} onChange={(v) => onChange({ ...p, y: v })} />
        </Grid>
        <Grid item xs={4}>
          <FieldNumber label="z" value={p.z} onChange={(v) => onChange({ ...p, z: v })} />
        </Grid>
      </Grid>
    </Box>
  );
}

function DimensionEditor({ dim, onChange }) {
  const d = dim ?? emptyDimension();
  return (
    <Box mt={1}>
      <Typography variant="subtitle2">dimension</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FieldNumber
            label="width"
            value={d.width}
            onChange={(v) => onChange({ ...d, width: v })}
          />
        </Grid>
        <Grid item xs={6}>
          <FieldNumber
            label="height"
            value={d.height}
            onChange={(v) => onChange({ ...d, height: v })}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

function AngleEditor({ label, angle, onChange }) {
  const a = angle ?? emptyAngle();
  return (
    <Box mt={1}>
      <Typography variant="subtitle2">{label}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <FieldNumber label="x" value={a.x} onChange={(v) => onChange({ ...a, x: v })} />
        </Grid>
        <Grid item xs={4}>
          <FieldNumber label="y" value={a.y} onChange={(v) => onChange({ ...a, y: v })} />
        </Grid>
        <Grid item xs={4}>
          <FieldNumber label="z" value={a.z} onChange={(v) => onChange({ ...a, z: v })} />
        </Grid>
      </Grid>
    </Box>
  );
}

function AnimationsEditor({ animations, onChange }) {
  const list = Array.isArray(animations) ? animations : [];

  const add = (type) => onChange([...list, makeDefaultAnimation(type)]);
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  const patch = (idx, nextAnim) => onChange(list.map((a, i) => (i === idx ? nextAnim : a)));

  return (
    <Box mt={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1">nodeAnimations (nur Button)</Typography>
        <Box display="flex" style={{ gap: 8 }}>
              <Button
                type="button"
                size="small"
                color="primary"
				variant="contained"
               className="transition_button"
                onClick={(e) => {
                  e.preventDefault();
                  add("transition");
                }}
              >
                + transition
              </Button>

              <Button
                type="button"
                size="small"
                color="primary"
				variant="contained"
              className="rotation_button"
                onClick={(e) => {
                  e.preventDefault();
                  add("rotation");
                }}
              >
                + rotation
              </Button>

              <Button
                type="button"
                size="small"
                color="primary"
				variant="contained"
              className="opacity_button"
                onClick={(e) => {
                  e.preventDefault();
                  add("opacity");
                }}
              >
                + opacity
              </Button>
            </Box>
          </Box>

      {list.length === 0 ? (
        <Typography variant="body2" style={{ opacity: 0.7, marginTop: 8 }}>
          Keine Animationen
        </Typography>
      ) : null}

      {list.map((a, idx) => (
        <Card key={idx} variant="outlined" style={{ marginTop: 12 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Animation #{idx + 1}</Typography>
              <Button size="small" className="remove_Button" color="primary"
				variant="contained" onClick={() => remove(idx)}>
                Remove
              </Button>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FieldSelect
                  label="animationType"
                  value={a.animationType}
                  options={ANIMATION_TYPES}
                  onChange={(t) => patch(idx, makeDefaultAnimation(t))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldNumber
                  label="duration"
                  value={a.duration}
                  step={0.1}
                  onChange={(v) => patch(idx, { ...a, duration: v })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldNumber
                  label="repeatCount"
                  value={a.repeatCount}
                  step={1}
                  onChange={(v) => patch(idx, { ...a, repeatCount: v })}
                />
              </Grid>
            </Grid>

            <Divider style={{ marginTop: 12, marginBottom: 12 }} />

            {a.animationType === "transition" && (
              <>
                <PositionEditor
                  label="fromPosition"
                  pos={a.fromPosition}
                  onChange={(p) => patch(idx, { ...a, fromPosition: p })}
                />
                <PositionEditor
                  label="toPosition"
                  pos={a.toPosition}
                  onChange={(p) => patch(idx, { ...a, toPosition: p })}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!a.autoreverses}
                      onChange={(e) => patch(idx, { ...a, autoreverses: e.target.checked })}
                    />
                  }
                  label="autoreverses"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!a.isRemovedOnCompletion}
                      onChange={(e) =>
                        patch(idx, { ...a, isRemovedOnCompletion: e.target.checked })
                      }
                    />
                  }
                  label="isRemovedOnCompletion"
                />
              </>
            )}

            {a.animationType === "rotation" && (
              <>
                <AngleEditor
                  label="fromAngle"
                  angle={a.fromAngle}
                  onChange={(ang) => patch(idx, { ...a, fromAngle: ang })}
                />
                <AngleEditor
                  label="toAngle"
                  angle={a.toAngle}
                  onChange={(ang) => patch(idx, { ...a, toAngle: ang })}
                />
              </>
            )}

            {a.animationType === "opacity" && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FieldNumber
                    label="fromOpacity"
                    value={a.fromOpacity}
                    step={0.05}
                    onChange={(v) => patch(idx, { ...a, fromOpacity: v })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldNumber
                    label="toOpacity"
                    value={a.toOpacity}
                    step={0.05}
                    onChange={(v) => patch(idx, { ...a, toOpacity: v })}
                  />
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}

export default function SceneEditor({ value, onChange }) {
    
  const [openAnimations, setOpenAnimations] = React.useState({});

  const toggleAnimations = (nodeId) => {
    setOpenAnimations(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };    
    
  const [openNodes, setOpenNodes] = React.useState({});  
    const toggleNode = (nodeKey) => {
      setOpenNodes(prev => ({
        ...prev,
        [nodeKey]: !prev[nodeKey]
      }));
    };
    
  const scene =
    value && typeof value === "object"
      ? value
      : { arSceneDescription: "", active: true, arNodes: [] };

  const nodes = Array.isArray(scene.arNodes) ? scene.arNodes : [];

  const patchScene = (partial) => onChange({ ...scene, ...partial });

  const addNode = (type) => patchScene({ arNodes: [...nodes, makeDefaultNode(type)] });

  const removeNode = (idx) => patchScene({ arNodes: nodes.filter((_, i) => i !== idx) });

  const patchNode = (idx, nextNode) =>
    patchScene({ arNodes: nodes.map((n, i) => (i === idx ? nextNode : n)) });

  const changeNodeType = (idx, type) => {
    const old = nodes[idx] || {};
    const next = makeDefaultNode(type);

    // keep identity-like fields if already set
    next.nodeId = old.nodeId ?? next.nodeId;
    next.nodeName = old.nodeName ?? next.nodeName;
    next.nodeActive = old.nodeActive ?? next.nodeActive;

    // IMPORTANT: animations only allowed for button
    if (type !== "button") {
      delete next.nodeAnimations;
    }

    patchNode(idx, next);
  };

  return (
    <Box mt={3} p={2} border="1px solid #ddd" borderRadius={10}>
      <Typography variant="h6">AR Scene Editor</Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <FieldText
            label="arSceneDescription"
            value={scene.arSceneDescription}
            onChange={(v) => patchScene({ arSceneDescription: v })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!scene.active}
                onChange={(e) => patchScene({ active: e.target.checked })}
              />
            }
            label="Scene active"
          />
        </Grid>
      </Grid>

      <Box mt={2} display="flex" gridGap={8} style={{ gap: 8 }}>
        <Button variant="standard" color="primaray" className="video_button" onClick={() => addNode("video")}>
          + Node (video)
        </Button>
        <Button variant="standard" color="primaray"  className="button_button" onClick={() => addNode("button")}>
          + Node (button)
        </Button>
      </Box>

      <Box mt={2}>
        {nodes.length === 0 ? (
          <Typography variant="body2" style={{ opacity: 0.7 }}>
            Noch keine arNodes
          </Typography>
        ) : null}

        {nodes.map((node, idx) => {
          const a = node.nodeAttributes ?? {};
          const isButton = node.nodeType === "button";
          const nodeKey = node.nodeId ? node.nodeId : `idx-${idx}`;

          return (
            <Card key={node.nodeId || idx} variant="outlined" style={{ marginTop: 12 }}>
              <CardContent>
                  
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Button
                      size="small"
                      variant="text"
                        className="flipButton"
                      onClick={() => toggleNode(nodeKey)}
                    >
                      {openNodes[nodeKey] ? "▼" : "▶"}
                    </Button>

                    <Typography variant="subtitle1">
                      Node #{idx + 1} ({node.nodeType})
                    </Typography>
                  </Box>

                  <Button
                    size="small"
                    className="remove_Button"
                    color="primary"
                    variant="contained"
                    onClick={() => removeNode(idx)}
                  >
                    Remove
                  </Button>
                </Box>
                
                  {openNodes[nodeKey] && (
          <>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FieldText
                      label="nodeName"
                      value={node.nodeName}
                      onChange={(v) => patchNode(idx, { ...node, nodeName: v })}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FieldText
                      label="nodeId"
                      value={node.nodeId}
                      onChange={(v) => patchNode(idx, { ...node, nodeId: v })}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FieldSelect
                      label="nodeType"
                      value={node.nodeType}
                      options={NODE_TYPES}
                      onChange={(t) => changeNodeType(idx, t)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!node.nodeActive}
                          onChange={(e) =>
                            patchNode(idx, { ...node, nodeActive: e.target.checked })
                          }
                        />
                      }
                      label="nodeActive"
                    />
                  </Grid>
                </Grid>

                <Divider style={{ marginTop: 12, marginBottom: 12 }} />
                <Typography variant="subtitle1">nodeAttributes</Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!a.maximize}
                      onChange={(e) =>
                        patchNode(idx, {
                          ...node,
                          nodeAttributes: { ...a, maximize: e.target.checked },
                        })
                      }
                    />
                  }
                  label="maximize"
                />

                <FieldText
                  label="externalLink"
                  value={a.externalLink}
                  onChange={(v) =>
                    patchNode(idx, { ...node, nodeAttributes: { ...a, externalLink: v } })
                  }
                />

                <PositionEditor
                  label="position"
                  pos={a.position}
                  onChange={(p) =>
                    patchNode(idx, { ...node, nodeAttributes: { ...a, position: p } })
                  }
                />

                {node.nodeType === "video" && (
                  <>
                    <FieldText
                      label="src (video url)"
                      value={a.src}
                      onChange={(v) =>
                        patchNode(idx, { ...node, nodeAttributes: { ...a, src: v } })
                      }
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!a.audio}
                          onChange={(e) =>
                            patchNode(idx, {
                              ...node,
                              nodeAttributes: { ...a, audio: e.target.checked },
                            })
                          }
                        />
                      }
                      label="audio"
                    />

                    <FieldText
                      label="audiosrc (audio url)"
                      value={a.audiosrc}
                      onChange={(v) =>
                        patchNode(idx, { ...node, nodeAttributes: { ...a, audiosrc: v } })
                      }
                    />
                  </>
                )}

               {isButton && (
  <>
    <FieldText
      label="src (image url)"
      value={a.src}
      onChange={(v) =>
        patchNode(idx, { ...node, nodeAttributes: { ...a, src: v } })
      }
    />

    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <FieldNumber
          label="opacity"
          value={typeof a.opacity === "number" ? a.opacity : 1}
          step={0.05}
          onChange={(v) =>
            patchNode(idx, { ...node, nodeAttributes: { ...a, opacity: v } })
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FieldText
          label="bgrcolor (#RRGGBB)"
          value={a.bgrcolor}
          onChange={(v) =>
            patchNode(idx, {
              ...node,
              nodeAttributes: { ...a, bgrcolor: v },
            })
          }
        />
      </Grid>
    </Grid>

    <DimensionEditor
      dim={a.dimension}
      onChange={(d) =>
        patchNode(idx, { ...node, nodeAttributes: { ...a, dimension: d } })
      }
    />

    {/* Toggle Button */}
    <Button
      type="button"
      size="small"
      variant="contained"
      className="animations_button"
      onClick={() => toggleAnimations(node.nodeId || idx)}
      style={{ marginTop: 12 }}
    >
      {openAnimations[node.nodeId || idx] ? "▼" : "▶"} Animationen (
      {(node.nodeAnimations ?? []).length})
    </Button>

    {/* AnimationsEditor einklappbar */}
    {openAnimations[node.nodeId || idx] && (
      <Box mt={1}>
        <AnimationsEditor
          animations={node.nodeAnimations ?? []}
          onChange={(nextAnims) =>
            patchNode(idx, { ...node, nodeAnimations: nextAnims })
          }
        />
      </Box>
    )}
  </>
)}

                 </>
        )}           
                          
                          
                          
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Box mt={2}>
        <Typography variant="subtitle2">JSON Preview</Typography>
        <pre style={{ background: "#f6f6f6", padding: 12, borderRadius: 8, overflow: "auto" }}>
          {JSON.stringify(scene, null, 2)}
        </pre>
      </Box>
    </Box>
  );
}
